import { Spinner, Text } from "@hackthenorth/north";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";

import {
  errorToast,
  Header,
  PageWrapper,
  successToast,
} from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { useGetHackerQuery } from "src/shared/contexts/HackerContext/graphql/getHacker.generated";
import { useGetMeQuery } from "src/shared/contexts/UserContext/graphql/getMe.generated";
import { GridAdd } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import ItemCard from "../components/cards/ItemCard";
import StoreProfileCard from "../components/cards/StoreProfileCard";
import LearnMoreModal from "../components/modals/hacker/home/learn_more";
import WelcomeBackModal from "../components/modals/hacker/home/welcome_back";
import HelpDeskModal from "../components/modals/hacker/store/map";
import CreatePrizeModal from "../components/modals/organizer/prize/create";
import { useGooseGamesContext } from "../context";
import { useCreateGooseGameStoreItemMutation } from "../graphql/createGooseGamePrize.generated";
import { useGetGooseGameItemsQuery } from "../graphql/getGooseGameItems.generated";
import { useMyGooseGamePlayerQuery } from "../graphql/hacker/myGooseGamePlayer.generated";
import { useUpdateGooseGamePrizeMutation } from "../graphql/updateGooseGamePrize.generated";
import { getPalNumberFromFields } from "../hackPals";
import { StoreItem } from "../types";

const GooseGamesStore: React.FC = () => {
  const {
    data: getItemsData,
    loading: fetchingItems,
    refetch: refetchItems,
  } = useGetGooseGameItemsQuery({ variables: { eventId: EVENT_ID } });

  const {
    showWelcomeBack,
    handleWelcomeBackClose,
    showLearnMore,
    handleLearnMoreOpen,
    handleLearnMoreClose,
  } = useGooseGamesContext();

  const [gooseGameItems, setGooseGameItems] = useState<StoreItem[]>([]);
  const [isCreatePrizeModalOpen, setIsCreatePrizeModalOpen] = useState(false);
  const [showHelpDeskModal, setShowHelpDeskModal] = useState(false);

  const { isOrganizer } = useUserContext();

  const [createGooseGameStoreItem] = useCreateGooseGameStoreItemMutation();
  const [updateGooseGamesStoreItems] = useUpdateGooseGamePrizeMutation();

  const { data: myProfileData } = useGetMeQuery();
  const { data: myGGPlayerData } = useMyGooseGamePlayerQuery();
  const { data: getHackerData } = useGetHackerQuery({
    variables: { myId: myProfileData ? myProfileData.me.id : -1 },
    skip: !myProfileData,
  });

  const myProfile = myProfileData?.me;

  const palNumber = getPalNumberFromFields(getHackerData?.claims?.[0]?.fields);

  const handleCreatePrize = async (
    name: string,
    pointCost: number,
    quantityAvailable: number,
    userRedeptionLimit: number,
    successMessage: string,
    errorMessage: string,
    description?: string,
    imageUrl?: string
  ) => {
    try {
      await createGooseGameStoreItem({
        variables: {
          name: name,
          eventId: EVENT_ID,
          description: description,
          imageUrl: imageUrl,
          pointCost: pointCost,
          quantityAvailable: quantityAvailable,
          userRedemptionLimit: userRedeptionLimit,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
    } finally {
      refetchItems();
    }
    setIsCreatePrizeModalOpen(false);
  };

  const toggleCompleted = async (itemId: number, isAvailable: boolean) => {
    try {
      await updateGooseGamesStoreItems({
        variables: {
          id: itemId,
          quantityAvailable: isAvailable ? 0 : 1,
        },
      });
    } catch (error) {
      errorToast(`Could not mark ${isAvailable ? "Sold Out" : "Available"}`);
    } finally {
      refetchItems();
    }
  };

  useEffect(() => {
    if (getItemsData && getItemsData.gooseGameStoreItems) {
      const mappedStoreItems: StoreItem[] =
        getItemsData.gooseGameStoreItems.map((item) => ({
          event_id: EVENT_ID,
          id: item.id,
          name: item.name,
          description: item.description ?? undefined,
          image_url: item.image_url ?? undefined,
          point_cost: item.point_cost,
          quantity_available: item.quantity_available,
          user_redemption_limit: item.user_redemption_limit,
          created_at: item.created_at,
          is_sold_out: item.is_sold_out,
        }));

      const availableItems = mappedStoreItems
        .filter((item) => !item.is_sold_out && item.quantity_available > 0)
        .sort((a, b) => a.point_cost - b.point_cost);
      const completedItems = mappedStoreItems
        .filter((item) => item.is_sold_out || item.quantity_available <= 0)
        .sort((a, b) => a.point_cost - b.point_cost);

      setGooseGameItems([...availableItems, ...completedItems]);
    }
  }, [getItemsData]);

  const learnMoreButton = (
    <StyledButton
      trailingIcon={<InformationCircleIcon width={18} />}
      size="md"
      color="tertiary"
      onClick={handleLearnMoreOpen}
    >
      Learn more
    </StyledButton>
  );

  return (
    <>
      <Header
        title="Goose Games"
        subtitle="Complete quests to collect points and redeem exclusive prizes!"
        actionButton={
          <>
            <DesktopBalanceContainer>
              <StoreProfileCard
                profile={myProfile}
                palNumber={palNumber}
                stackDetails
                ggPlayer={
                  myGGPlayerData ? myGGPlayerData.myGooseGamePlayer : undefined
                }
              />
            </DesktopBalanceContainer>
            <MobileLearnMoreContainer>
              {learnMoreButton}
            </MobileLearnMoreContainer>
          </>
        }
      />
      <StyledPageWrapper>
        <SubtitleContainer>
          {!isOrganizer && (
            <MobileBalanceContainer>
              <TitleText mods="heading h3">My Balance</TitleText>
              <StoreProfileCard
                profile={myProfile}
                palNumber={palNumber}
                ggPlayer={
                  myGGPlayerData ? myGGPlayerData.myGooseGamePlayer : undefined
                }
              />
            </MobileBalanceContainer>
          )}
          <HeaderFlex>
            <TitleText mods="heading h3">Prizes</TitleText>
            {isOrganizer && (
              <Button
                leadingIcon={<GridAdd width={12.8} />}
                size="md"
                color="primary"
                onClick={() => setIsCreatePrizeModalOpen(true)}
              >
                Create new prize
              </Button>
            )}
          </HeaderFlex>
          {!isOrganizer && (
            <DescriptionLine>
              <Text mods="p">
                Spend the points you earn from completing quests to redeem
                prizes! Prizes are only redeemable in-person at Help Desk.
              </Text>
            </DescriptionLine>
          )}
        </SubtitleContainer>
        <QuestCardGrid>
          {fetchingItems ? (
            <Spinner />
          ) : (
            gooseGameItems.map((item) => (
              <Fragment key={item.id}>
                <ItemCard
                  item={item}
                  toggleCompleted={toggleCompleted}
                  refetchItems={refetchItems}
                />
              </Fragment>
            ))
          )}
        </QuestCardGrid>
      </StyledPageWrapper>

      {/* Modals */}
      <CreatePrizeModal
        isOpen={isCreatePrizeModalOpen}
        onConfirm={handleCreatePrize}
        onCancel={() => setIsCreatePrizeModalOpen(false)}
      />
      <HelpDeskModal
        isOpen={showHelpDeskModal}
        onClose={() => setShowHelpDeskModal(false)}
      />
      {!showLearnMore && showWelcomeBack && (
        <WelcomeBackModal
          isOpen={showWelcomeBack}
          onClose={handleWelcomeBackClose}
        />
      )}
      {showLearnMore && (
        <LearnMoreModal isOpen={showLearnMore} onClose={handleLearnMoreClose} />
      )}
    </>
  );
};

const TitleText = styled(Text)`
  color: #374151 !important;
`;

const StyledPageWrapper = styled(PageWrapper)`
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
`;

const HeaderFlex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const SubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
`;

const MobileBalanceContainer = styled(SubtitleContainer)`
  display: none;

  ${mediaQueries.largeMobile} {
    display: flex;
  }
`;

const DesktopBalanceContainer = styled.div`
  width: fit-content;
  max-width: 360px;

  ${mediaQueries.largeMobile} {
    display: none;
  }
`;

const MobileLearnMoreContainer = styled.div`
  display: none;

  ${mediaQueries.largeMobile} {
    display: block;
  }
`;

const QuestCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  padding-bottom: 24px;

  ${mediaQueries.medium} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const DescriptionLine = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary} !important;
  padding: 0 !important;
`;

export default GooseGamesStore;
